import { Reveal } from "@/components/site/Reveal";
import { PageHeader } from "@/components/site/PageParts";
import { SITE } from "@/siteData";
import { useMeta } from "@/useMeta";

const P = ({ children }) => (
  <Reveal>
    <p className="text-base leading-[1.8] text-ink-soft">{children}</p>
  </Reveal>
);

const H = ({ children }) => (
  <Reveal>
    <h2 className="!mt-12 font-display text-xl font-bold tracking-tight text-ink sm:text-2xl">{children}</h2>
  </Reveal>
);

export default function Terms() {
  useMeta({
    title: "Terms of Use | Simple Background Remover",
    description:
      "Terms of Use for Simple Background Remover, provided by Hildreth Technologies LLC.",
  });

  return (
    <>
      <PageHeader index="/terms" eyebrow="Legal" title="Terms of Use" effective={SITE.effectiveDate} />
      <div className="container-x max-w-3xl py-16 sm:py-20">
        <div className="space-y-6">
          <P>
            These Terms of Use govern use of Simple Background Remover, provided by Hildreth
            Technologies LLC.
          </P>
          <P>
            Simple Background Remover allows users to process selected photographs locally and replace
            existing backgrounds with white or black.
          </P>

          <H>Users are responsible for:</H>
          <Reveal>
            <ul className="mt-2 space-y-2">
              {[
                "Having the right to use the photographs they select",
                "Reviewing completed images for accuracy",
                "Maintaining backups of original photographs",
                "Ensuring completed images comply with applicable laws and marketplace rules",
              ].map((it) => (
                <li key={it} className="flex items-start gap-3 text-base leading-relaxed text-ink-soft">
                  <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-brand" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <P>
            Automatic background removal may not correctly separate every object, edge, shadow, strap,
            lace detail, or background. Hildreth Technologies LLC does not guarantee perfect results for
            every photograph.
          </P>
          <P>
            The application is designed to create new completed files rather than overwrite original
            photographs. Users remain responsible for maintaining backups.
          </P>
          <H>Subscription Terms</H>
          <Reveal>
            <ul className="mt-2 space-y-2">
              {[
                "Monthly subscription: $4.99 per month.",
                "Annual subscription: $39.99 per year.",
                "No free trial is offered.",
                "Payment is charged through Google Play or Apple when the subscription purchase is confirmed.",
              ].map((it) => (
                <li key={it} className="flex items-start gap-3 text-base leading-relaxed text-ink-soft">
                  <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-brand" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <P>
            Users may not use the application to violate copyrights, trademarks, privacy rights,
            publicity rights, or applicable laws.
          </P>
          <P>
            The application is provided on an “as is” and “as available” basis to the extent permitted
            by law.
          </P>
          <P>
            To the extent permitted by law, Hildreth Technologies LLC is not responsible for indirect
            or consequential losses resulting from use of the application, failed processing, loss of
            locally stored files, or publication of completed photographs.
          </P>
          <P>
            These Terms may be updated when the application or applicable legal requirements change.
          </P>

          <H>Questions</H>
          <Reveal>
            <div className="mt-2 rounded-2xl border border-line bg-paper-2 p-6">
              <p className="text-sm text-ink-soft">Questions may be sent to:</p>
              <a
                href={`mailto:${SITE.email}`}
                className="link-underline mt-2 inline-block font-mono text-base text-brand"
                data-testid="terms-email"
              >
                {SITE.email}
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </>
  );
}
